/**
 * Componente de Popup de Avisos Internos
 *
 * Componente global que verifica periodicamente por avisos
 * internos pendentes e exibe um popup para que o usuario
 * confirme a leitura. Apos confirmar, avanca para o proximo
 * aviso pendente ou fecha automaticamente.
 *
 * Deve ser montado no layout principal da aplicacao.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell,
  X,
  Paperclip,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { api } from '@services/api';
import toast from 'react-hot-toast';
import { usePlanAccess } from '@hooks/usePlanAccess';

/** Aviso pendente */
interface PendingNotice {
  id: string;
  title: string;
  description: string;
  targetAudience: string;
  displayDate: string;
  isPinned: boolean;
  attachments?: Array<string | { url: string; name: string; type: string }>;
}

/** Intervalo de polling em milissegundos (60 segundos) */
const POLL_INTERVAL = 60000;

export function NoticePopup() {
  const [pendingNotices, setPendingNotices] = useState<PendingNotice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Avisos internos pertencem ao módulo de RH do plano
  const { hasModule, loading: planLoading } = usePlanAccess();
  const hrEnabled = !planLoading && hasModule('hr');

  /** Verifica se o usuario esta autenticado */
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('vendeai_access_token');
  }, []);

  /** Busca avisos pendentes */
  const fetchPendingNotices = useCallback(async () => {
    if (!isAuthenticated()) return;

    try {
      const res = await api.get('/hr/notices/pending');
      if (res.data.success && res.data.data && res.data.data.length > 0) {
        setPendingNotices(res.data.data);
        setCurrentIndex(0);
        setShowPopup(true);
      }
    } catch {
      // Silencioso - nao mostrar erro para polling de background
    }
  }, [isAuthenticated]);

  /** Inicia polling */
  useEffect(() => {
    if (!hrEnabled) return;
    // Busca inicial
    fetchPendingNotices();

    // Configura polling
    intervalRef.current = setInterval(fetchPendingNotices, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchPendingNotices, hrEnabled]);

  /** Confirma leitura do aviso atual */
  const handleAcknowledge = async () => {
    const currentNotice = pendingNotices[currentIndex];
    if (!currentNotice) return;

    setAcknowledging(true);
    try {
      await api.post(`/hr/notices/${currentNotice.id}/acknowledge`);

      // Move para o proximo aviso ou fecha
      if (currentIndex < pendingNotices.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setShowPopup(false);
        setPendingNotices([]);
        setCurrentIndex(0);
      }
    } catch {
      toast.error('Erro ao confirmar leitura do aviso');
    } finally {
      setAcknowledging(false);
    }
  };

  /** Fecha o popup sem confirmar (apenas fecha visualmente, voltara no proximo poll) */
  const handleDismiss = () => {
    setShowPopup(false);
  };

  // Nao renderiza se nao houver popup
  if (!showPopup || pendingNotices.length === 0) return null;

  const currentNotice = pendingNotices[currentIndex];
  if (!currentNotice) return null;

  const hasMultiple = pendingNotices.length > 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Banner Header */}
        <div className="rounded-t-xl bg-gradient-to-r from-primary-600 to-primary-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white/20 p-2">
                <Bell size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white/80">Aviso Interno</h3>
                {hasMultiple && (
                  <p className="text-xs text-white/60">
                    {currentIndex + 1} de {pendingNotices.length} aviso(s) pendente(s)
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="rounded-full p-1 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
              title="Fechar (voltara a aparecer)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Conteudo */}
        <div className="p-5 space-y-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {currentNotice.title}
          </h2>

          <div className="max-h-48 overflow-y-auto rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {currentNotice.description}
            </p>
          </div>

          {/* Anexos */}
          {currentNotice.attachments && currentNotice.attachments.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Anexos:</p>
              {currentNotice.attachments.map((att, i) => {
                const url = typeof att === 'string' ? att : att.url;
                const name = typeof att === 'string' ? att.split('/').pop() : att.name;
                return (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm text-primary-600 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50"
                  >
                    <Paperclip size={14} />
                    <span className="truncate">{name || `Anexo ${i + 1}`}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 p-4 dark:border-gray-700">
          {hasMultiple && currentIndex < pendingNotices.length - 1 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mais {pendingNotices.length - currentIndex - 1} aviso(s) pendente(s)
            </p>
          ) : (
            <div />
          )}
          <button
            onClick={handleAcknowledge}
            disabled={acknowledging}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {acknowledging ? (
              <Loader2 size={16} className="animate-spin" />
            ) : null}
            <span>OK, Li o aviso</span>
            {hasMultiple && currentIndex < pendingNotices.length - 1 && (
              <ChevronRight size={16} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
